import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Pluralize from 'react-pluralize';
import { FaTimes } from 'react-icons/fa';
import { useAnalytics } from 'use-analytics';
import { carSearch, updateCarSearchObj } from '../../../store/slices/car';
import CarsFilterBar from './CarsFilterBar';
import CarsList from './CarsList';
import CarSearchBar from './CarSearchBar';
import CarItinerary from './CarItinerary';
import {
  FiltersInitialValue,
  InfoPageSize,
  ListPageSize,
  SearchStatus,
  CarUISearchType,
  PageLinks,
  MaxRentalCarsInTrip,
} from '../../../constants';
import LimitText from '../../LimitText';
import withSpinner from '../../../HOCs/withSpinner';
import ModalContent from './ModalContent';
import formatTabLabel from './formatTabLabel';
import formatSearchObj from './formatSearchObj';
import CarsSorting from './CarsSorting';
// TODO: do you really need the CarsInformation or could you put the contents of the modal into
// a component (e.g. CarsModalContent with filters/list/pagination) and
// use that in agenttips/carsinformation? same applies to hotel/car/flight
const CarsInformation = ({
  tripId,
  dropoffCode,
  pickupCode,
  pickupDateTime,
  returnDateTime,
  onModalClose,
  doShowAsModalOnly,
  cruiseItineraryId,
  travelOption,
}) => {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const trip = useSelector((store) => store.trip.selectedTrips[tripId]);
  const cars = useSelector((store) => store.car.carSearches[tripId]);
  const isTripConfigured = trip && !!trip.cruiseItinerary && !!trip.flightItinerary;
  const isCarSearchExist = cars && cars.carTabs && cars.carTabs[selectedTab];
  const selectedCarsCount = useMemo(() => {
    if (trip && trip.rentalCarItineraries) {
      return Object.values(trip.rentalCarItineraries).length;
    }
    return 0;
  }, [trip]);
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { pageName } = router.query;
  const doAddChangeCarBookedTrip =
    pageName === PageLinks.ChangeCar.pageName || pageName === PageLinks.AddCar.pageName;

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    track('carsInformation.handleOpenModal');
  }, [track]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    if (onModalClose) {
      onModalClose(null);
    }
    track('carsInformation.handleCloseModal');
  }, [onModalClose, track]);

  const handleTabSelection = useCallback(
    (newValue) => {
      setSelectedTab(parseInt(newValue, 10));
      track('carsInformation.handleTabSelection', { newValue });
    },
    [track],
  );

  const handleAddCarSegment = useCallback(() => {
    const targetIndex = Object.values(cars.carTabs).pop().value + 1;
    dispatch(
      updateCarSearchObj({
        tripId,
        carTabs: {
          ...cars.carTabs,
          [targetIndex]: {
            label: 'New segment',
            value: targetIndex,
            searchObj: FiltersInitialValue.car,
            searchStatus: SearchStatus.INITIAL,
            minBudget: null,
            maxBudget: null,
          },
        },
        defaultSearchObj: FiltersInitialValue.car,
      }),
    );
    setSelectedTab(targetIndex);
    track('carsInformation.handleAddCarSegment');
  }, [cars, tripId, dispatch, track]);

  const handleRemoveCarSegment = useCallback(
    (target) => {
      const newTabs = { ...cars.carTabs };
      const tabIndexes = Object.keys(newTabs);
      const targetIndex = Object.keys(newTabs).findIndex((item) => parseInt(item, 10) === target);
      setSelectedTab((previous) => {
        if (previous > target) {
          return previous;
        }
        if (targetIndex === 0) {
          return parseInt(tabIndexes[targetIndex + 1], 10);
        }
        return parseInt(tabIndexes[targetIndex - 1], 10);
      });
      delete newTabs[target];
      dispatch(
        updateCarSearchObj({
          tripId,
          carTabs: newTabs,
        }),
      );
      track('carsInformation.handleRemoveCarSegment');
    },
    [cars, tripId, dispatch, track],
  );

  useEffect(() => {
    if (!doShowAsModalOnly) {
      const tabs = formatSearchObj(travelOption, cars, isCarSearchExist, trip, cruiseItineraryId);
      dispatch(
        updateCarSearchObj({
          tripId,
          minBudget: null,
          maxBudget: null,
          carTabs: { ...tabs },
        }),
      );
      if (tabs[selectedTab || 0].searchObj.pickupCode) {
        dispatch(
          carSearch({
            tripId,
            selectedTab,
          }),
        );
      }
    } else {
      dispatch(
        updateCarSearchObj({
          tripId,
          minBudget: null,
          maxBudget: null,
          carTabs: {
            0: {
              label: formatTabLabel(
                pickupCode.label,
                dropoffCode.label,
                pickupDateTime,
                returnDateTime,
              ),
              value: 0,
              searchStatus: SearchStatus.INITIAL,
              searchObj: {
                ...FiltersInitialValue.car,
                carSearchType: CarUISearchType.BEFOREANDAFTERCRUISE,
                pageSize: ListPageSize,
                pickupDateTime,
                returnDateTime,
                dropoffCode,
                pickupCode,
              },
            },
          },
        }),
      );
      dispatch(
        carSearch({
          tripId,
          selectedTab,
        }),
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isCarSearchExist &&
      ((isTripConfigured && (selectedTab === 0 || selectedTab === 1)) ||
        cars.carTabs[selectedTab].queryString)
    ) {
      dispatch(
        carSearch({
          tripId,
          selectedTab,
          searchObj: {
            pageSize: isModalOpen ? ListPageSize : InfoPageSize,
          },
        }),
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isModalOpen, selectedTab]);
  useSelector((store) => store.tripSearch.searchObj.car);
  const renderContent = () =>
    cars.carTabs[selectedTab].searchStatus === SearchStatus.DONE ? (
      <>
        <div className="font-bold mt-4 mb-2">Filter by</div>
        <CarsFilterBar tripId={tripId} selectedTab={selectedTab} />
        <div className="mt-4 mb-2 font-bold flex justify-between items-center">
          Best cars
          <CarsSorting tripId={tripId} selectedTab={selectedTab} />
        </div>
        <CarsList onViewMoreClick={handleOpenModal} tripId={tripId} selectedTab={selectedTab} />
      </>
    ) : (
      ''
    );

  return (
    <div className="mx-5 my-5">
      {!doAddChangeCarBookedTrip && selectedCarsCount ? (
        <>
          <div className="mt-10 mb-5 font-bold">
            Selected <Pluralize singular="car" count={selectedCarsCount} showCount={false} />
          </div>
          {Object.entries(trip.rentalCarItineraries).map(([key, value]) => (
            <CarItinerary
              key={`${value.id}`}
              itinerary={value}
              tripId={tripId}
              onModalClose={onModalClose}
              selectedTab={key}
            />
          ))}
        </>
      ) : (
        ''
      )}
      {!doAddChangeCarBookedTrip && (
        <div className="flex text-sm">
          {isCarSearchExist &&
            Object.values(cars.carTabs).map((item) => (
              <div
                key={item.value}
                className={clsx(
                  {
                    'border border-b-0 rounded-md rounded-b-none': selectedTab === item.value,
                    'border-b': selectedTab !== item.value,
                  },
                  'px-2 flex py-2',
                )}
              >
                <button
                  type="button"
                  className="focus:outline-none h-full border-0"
                  value={item.value}
                  onClick={() => handleTabSelection(item.value)}
                >
                  <LimitText text={item.label} limit={40} />
                </button>
                {Object.values(cars.carTabs).length > 1 && (
                  <div className="flex-1 text-right ml-2">
                    <button
                      type="button"
                      className="focus:outline-none h-full"
                      value={item.value}
                      onClick={() => handleRemoveCarSegment(item.value)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            ))}
          {(!isCarSearchExist || Object.values(cars.carTabs).length < MaxRentalCarsInTrip) && (
            <div className="flex-1 border border-r-0 border-l-0 border-t-0">
              <button
                type="button"
                className="border-1 bg-white px-4 focus:outline-none rounded-2xl border-theme text-theme h-full"
                onClick={handleAddCarSegment}
              >
                + Add car segment
              </button>
            </div>
          )}
        </div>
      )}
      {isCarSearchExist && cars.carTabs[selectedTab].searchObj && (
        <>
          <CarSearchBar tripId={tripId} selectedTab={selectedTab} />
          <div
            className={clsx({
              'mt-10': cars.carTabs[selectedTab].searchStatus === SearchStatus.SEARCHING,
            })}
          >
            {withSpinner(
              renderContent,
              cars.carTabs[selectedTab].searchStatus === SearchStatus.SEARCHING,
            )}
            <ModalContent
              tripId={tripId}
              selectedTab={selectedTab}
              doAddChangeCarBookedTrip={doAddChangeCarBookedTrip}
              isModalOpen={isModalOpen}
              onCloseModal={handleCloseModal}
              doShowAsModalOnly={doShowAsModalOnly}
              onModalClose={onModalClose}
            />
          </div>
        </>
      )}
    </div>
  );
};

CarsInformation.propTypes = {
  tripId: PropTypes.string.isRequired,
  pickupCode: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    group: PropTypes.string,
    locationType: PropTypes.string,
  }),
  dropoffCode: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    group: PropTypes.string,
    locationType: PropTypes.string,
  }),
  pickupDateTime: PropTypes.string,
  returnDateTime: PropTypes.string,
  doShowAsModalOnly: PropTypes.bool,
  onModalClose: PropTypes.func,
  cruiseItineraryId: PropTypes.string,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
    itineraryDepartCity: PropTypes.string,
    itinerary: PropTypes.shape(),
    itineraryRows: PropTypes.arrayOf(PropTypes.shape()),
    flightItinerary: PropTypes.shape({
      flightSegments: PropTypes.arrayOf(PropTypes.shape()),
    }),
    hotelItinerary: PropTypes.shape({
      id: PropTypes.number,
    }),
    carItinerary: PropTypes.shape({
      id: PropTypes.number,
      destinationGeoname: PropTypes.shape(),
    }),
  }).isRequired,
};

CarsInformation.defaultProps = {
  pickupCode: undefined,
  dropoffCode: undefined,
  pickupDateTime: undefined,
  returnDateTime: undefined,
  doShowAsModalOnly: false,
  onModalClose: undefined,
  cruiseItineraryId: undefined,
};

export default memo(CarsInformation);
